import type { PlanningTaskMetadata, Task } from '../services/taskService';

import {
  isPlanningRetryScope, planningActionStages, type PlanningActionScope, type PlanningActionTaskRow,
} from './planningActionTasks';
import { orderedPlanningTasks } from './planningTaskPresentation';

interface TaskListSnapshot {
  kind?: 'plan' | 'result';
  title: string;
  status: Task['status'];
  execution_step?: string;
  execution_progress?: number;
  started_at?: string | null;
  completed_at?: string | null;
  closed_at?: string;
  task_number?: number | null;
  position?: number;
  prompt_credit?: number;
  planning_credit_charged?: boolean;
  planning_metadata?: Task['planning_metadata'];
}

const liveBillingMetadataKeys = [
  'credit_price_captured',
  'credit_price_kind',
  'credit_charge_consumed',
  'credit_not_required',
  'credit_billing_user_id',
  'active_credit_billing_user_id',
  'credit_price_source',
] as const;

const phaseNames: Record<string, string> = {
  srd: 'Requirements', user_flows: 'Personas & user flows',
  design_first_page: 'First page design', design_remaining: 'Remaining page designs',
  architecture: 'Architecture', pm: 'Workspace task plan',
  artifact_updates: 'Artifact updates',
};

const snapshotFor = (task: Task, entryId: string): TaskListSnapshot | undefined => {
  const snapshots = task.planning_metadata?.task_list_snapshots;

  if (!snapshots || typeof snapshots !== 'object' || Array.isArray(snapshots)) return undefined;
  const snapshot = (snapshots as Record<string, TaskListSnapshot>)[entryId];

  return snapshot && typeof snapshot.title === 'string' && typeof snapshot.status === 'string'
    ? snapshot : undefined;
};

const ownsEntry = (task: Task, entryId: string): boolean => {
  const entries = task.planning_metadata?.task_list_entry_ids;

  return (Array.isArray(entries) && entries.includes(entryId)) || Boolean(snapshotFor(task, entryId));
};

const historicalTask = (task: Task, entryId: string): Task => {
  const snapshot = snapshotFor(task, entryId);

  if (!snapshot) return task;

  const liveMetadata = task.planning_metadata ?? {};
  const historicalMetadata: PlanningTaskMetadata = {
    ...(snapshot.planning_metadata ?? {
      run_id: liveMetadata.run_id, stage: liveMetadata.stage,
      artifact_type: liveMetadata.artifact_type, artifact_name: liveMetadata.artifact_name,
      parent_key: liveMetadata.parent_key,
    }),
  };

  // Billing is settled after a transcript snapshot can be captured. Keep the
  // snapshot's execution fields, but make its billing metadata follow the live task.
  liveBillingMetadataKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(liveMetadata, key)) {
      Object.assign(historicalMetadata, { [key]: liveMetadata[key] });
    }
    else delete historicalMetadata[key];
  });

  return {
    ...task, ...snapshot,
    // Transcript snapshots freeze execution history, but billing state follows
    // the task's one-time debit.  A credits update refetches the live task after
    // admission (or compensation), so keep only these credit fields live while
    // preserving the snapshot's status, selection metadata and timing.
    prompt_credit: task.prompt_credit,
    planning_credit_charged: task.planning_credit_charged,
    started_at: snapshot.started_at ?? undefined,
    completed_at: snapshot.completed_at ?? snapshot.closed_at,
    planning_metadata: historicalMetadata,
  };
};

const legacyOwner = (task: Task, entryId: string): boolean => {
  const metadata = task.planning_metadata;
  const entries = metadata?.trigger_entry_ids;

  // New records explicitly declare membership, including an intentionally empty list.
  if (Array.isArray(metadata?.task_list_entry_ids)) return false;

  return metadata?.trigger_message_id === entryId || metadata?.trigger_entry_id === entryId
    || (Array.isArray(entries) && entries.includes(entryId));
};

/** Display ownership is separate from the live selection that drives action execution. */
// eslint-disable-next-line max-statements
export const planningTaskMessage = (
  tasks: Task[], scope: PlanningActionScope, liveRows: PlanningActionTaskRow[],
): { rows: PlanningActionTaskRow[]; archived: boolean; savedPlan: boolean; proposed: boolean; heading: string } => {
  const entryId = scope.entryId;
  const projectTasks = tasks.filter((task) => task.task_type === 'planning' && task.project_id === scope.projectId);
  const owned = entryId ? projectTasks.filter((task) => ownsEntry(task, entryId)) : [];
  let rows: PlanningActionTaskRow[] = [];
  let proposed = false;

  if (entryId && owned.length) {
    const closedAt = new Map(owned.map((task) => [task.id, snapshotFor(task, entryId)?.closed_at]));
    // A plan frozen at click that already showed a task as done listed work finished
    // before this action; older records carry those rows, newer ones never add them.
    const open = owned.filter((task) => !(snapshotFor(task, entryId)?.kind === 'plan'
      && snapshotFor(task, entryId)?.status === 'done'
      && task.planning_metadata?.artifact_type !== 'stage'));

    rows = orderedPlanningTasks(open.map((task) => historicalTask(task, entryId)))
      .map((task) => ({
        id: task.id, title: task.title, task,
        snapshotAt: closedAt.get(task.id),
      }));
  } else if (!scope.historyOnly) {
    const stages = planningActionStages(scope);
    const legacy = entryId ? projectTasks.filter((task) => legacyOwner(task, entryId)
      && stages.includes(task.planning_metadata?.stage as typeof stages[number])) : [];

    rows = scope.current || scope.followActive ? liveRows : orderedPlanningTasks(legacy)
      .map((task) => ({ id: task.id, title: task.title, task }));
    proposed = Boolean(scope.current && !scope.followActive && !legacy.length);
    // An unclicked retry offer is upcoming work, not another execution receipt.
    // Once that work finishes elsewhere, the old offer must not fabricate Ready
    // rows or continue presenting completed work as something to retry.
    if (proposed && isPlanningRetryScope(scope)) {
      rows = rows.filter((row) => !row.task || !['done', 'manual_review', 'invalid'].includes(row.task.status));
    }
  }
  const stages = Array.from(new Set(rows.map((row) => row.task?.planning_metadata?.stage
    ?? row.id.replace(/^planned:/, ''))));
  const phases = Object.keys(phaseNames).filter((stage) => stages.includes(stage)).map((stage) => phaseNames[stage]);

  return {
    rows,
    archived: Boolean(entryId && owned.length && owned.every((task) => snapshotFor(task, entryId))),
    savedPlan: Boolean(entryId && owned.length && owned.every((task) => snapshotFor(task, entryId)?.kind === 'plan')),
    proposed: proposed && rows.length > 0,
    heading: phases.length ? `Planning tasks · ${phases.join(' / ')}` : 'Planning tasks',
  };
};
